export const exportInternshipsToCSV = (internships, interns, mentors) => {
    // Helper function to safely get intern data
    const getInternData = (studentUid) => {
        const intern = interns?.find(i => i.uid === studentUid);
        if (!intern) return { firstName: 'Unknown', lastName: '', school: '', location: '', interests: '' };

        // Check if firstName and lastName exist, otherwise use name field
        let firstName = '';
        let lastName = '';

        if (intern.firstName) {
            firstName = intern.firstName;
            lastName = intern.lastName || '';
        } else if (intern.name) {
            // If only name field exists, use it as firstName
            firstName = intern.name;
            lastName = '';
        }

        return {
            firstName: firstName || '',
            lastName: lastName || '',
            school: intern.school || '',
            location: intern.location || '',
            interests: intern.interests || ''
        };
    };

    // Helper function to safely get mentor data
    const getMentorData = (mentorUid) => {
        const mentor = mentors?.find(m => m.uid === mentorUid);
        if (!mentor) return { firstName: 'Unknown', lastName: '', organization: '' };

        // Check if firstName and lastName exist, otherwise use name field
        let firstName = '';
        let lastName = '';

        if (mentor.firstName) {
            firstName = mentor.firstName;
            lastName = mentor.lastName || '';
        } else if (mentor.name) {
            // If only name field exists, use it as firstName
            firstName = mentor.name;
            lastName = '';
        }

        return {
            firstName: firstName || '',
            lastName: lastName || '',
            organization: mentor.organization || mentor.company || ''
        };
    };

    // Format date for CSV
    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        return new Date(timestamp).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        });
    };

    // Convert goals/tasks array to comma-separated string
    const arrayToString = (arr) => {
        if (!arr || !Array.isArray(arr)) return '';
        return arr.map((item, index) => `${index + 1}. ${item}`).join('; ');
    };

    // Get status based on dates
    const getStatus = (startDate, endDate) => {
        const now = Date.now();
        const start = startDate ? new Date(startDate).getTime() : null;
        const end = endDate ? new Date(endDate).getTime() : null;

        if (!start) return 'Not Started';
        if (now < start) return 'Upcoming';
        if (end && now > end) return 'Completed';
        return 'Active';
    };

    // Create CSV headers
    const headers = [
        'Internship ID',
        'Internship Title',
        'Status',
        'Intern First Name',
        'Intern Last Name',
        'Intern School',
        'Intern Location',
        'Intern Interests',
        'Mentor First Name',
        'Mentor Last Name',
        'Mentor Organization',
        'Start Date',
        'End Date',
        'Goals',
        'Tasks',
        'Created Date'
    ];

    // Create CSV rows
    const rows = internships.map((internship, index) => {
        const internData = getInternData(internship.studentUid);
        const mentorData = getMentorData(internship.mentorUid);

        return [
            internship.uid,
            internship.title || `Internship ${index + 1}`,
            getStatus(internship.startDate, internship.endDate),
            internData.firstName,
            internData.lastName,
            internData.school,
            internData.location,
            internData.interests,
            mentorData.firstName,
            mentorData.lastName,
            mentorData.organization,
            formatDate(internship.startDate),
            formatDate(internship.endDate),
            arrayToString(internship.goals),
            arrayToString(internship.tasks),
            formatDate(internship.createdAt)
        ];
    });

    // Convert to CSV format
    const csvContent = [
        headers.join(','),
        ...rows.map(row =>
            row.map(cell => {
                // Escape quotes and wrap in quotes if contains comma or newline
                const cellStr = String(cell || '');
                if (cellStr.includes(',') || cellStr.includes('\n') || cellStr.includes('"')) {
                    return `"${cellStr.replace(/"/g, '""')}"`;
                }
                return cellStr;
            }).join(',')
        )
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const fileName = `internships_export_${new Date().toISOString().split('T')[0]}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const exportUnmatchedInternsToCSV = (unmatchedInterns, mentors) => {
    // Helper function to safely get mentor names from applications
    const getMentorNames = (mentorApplications, mentors) => {
        if (!mentorApplications || mentorApplications.length === 0) return '';

        const mentorNames = mentorApplications.map(app => {
            const mentor = mentors?.find(m => m.uid === app.mentorUid);
            if (!mentor) return 'Unknown';

            let name = '';
            if (mentor.firstName) {
                name = `${mentor.firstName} ${mentor.lastName || ''}`.trim();
            } else if (mentor.name) {
                name = mentor.name;
            }
            return name || 'Unknown';
        });

        return mentorNames.join('; ');
    };

    // Format date for CSV
    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        return new Date(timestamp).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        });
    };

    // Create CSV headers
    const headers = [
        'Intern ID',
        'First Name',
        'Last Name',
        'Email',
        'School',
        'Location',
        'Interests',
        'Job Title',
        'Number of Applications',
        'Applied to Mentors',
        'Application Date'
    ];

    // Create CSV rows
    const rows = unmatchedInterns.map((intern) => {
        // Handle name fields
        let firstName = '';
        let lastName = '';

        if (intern.firstName) {
            firstName = intern.firstName;
            lastName = intern.lastName || '';
        } else if (intern.name) {
            firstName = intern.name;
            lastName = '';
        }

        return [
            intern.uid,
            firstName,
            lastName,
            intern.email || '',
            intern.school || '',
            intern.location || '',
            intern.interests || '',
            intern.jobTitle || '',
            intern.mentorApplications?.length || 0,
            getMentorNames(intern.mentorApplications, mentors),
            formatDate(intern.createdAt)
        ];
    });

    // Convert to CSV format
    const csvContent = [
        headers.join(','),
        ...rows.map(row =>
            row.map(cell => {
                // Escape quotes and wrap in quotes if contains comma or newline
                const cellStr = String(cell || '');
                if (cellStr.includes(',') || cellStr.includes('\n') || cellStr.includes('"')) {
                    return `"${cellStr.replace(/"/g, '""')}"`;
                }
                return cellStr;
            }).join(',')
        )
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const fileName = `unmatched_interns_export_${new Date().toISOString().split('T')[0]}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};